import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable, tap} from 'rxjs';
import {
  HubEbicsSettings,
  HubDonationFormEditorResponse,
  HubForm,
  HubLogsResponse,
  HubLoginFormResponse,
  HubManagedUser,
  HubProcess,
  HubProcessEditorResponse,
  HubSession,
  HubSettingsResponse,
  HubTranslationsImportResponse,
  HubTranslationsResponse
} from '../model/hub-session';

export const CSRF_HEADER = 'X-GV-CSRF-Token';

interface FormsResponse {
  forms: HubForm[];
}

interface ProcessesResponse {
  processes: HubProcess[];
  peerEnvironment: 'prod' | 'test';
}

interface UsersResponse {
  users: HubManagedUser[];
}

interface EbicsResponse {
  ebics: HubEbicsSettings;
}

@Injectable({providedIn: 'root'})
export class HubApiService {
  private readonly apiBase = '/rest';
  private readonly sessionSubject = new BehaviorSubject<HubSession | null>(null);
  readonly session$ = this.sessionSubject.asObservable();

  constructor(private http: HttpClient) {
  }

  get currentSession(): HubSession | null {
    return this.sessionSubject.value;
  }

  loadSession(): Observable<HubSession> {
    return this.http.get<HubSession>(`${this.apiBase}/session.php`, {withCredentials: true}).pipe(
      tap((session) => this.sessionSubject.next(session))
    );
  }

  login(login: string, password: string): Observable<HubSession> {
    return this.http.post<HubSession>(`${this.apiBase}/login.php`, {login, password}, {withCredentials: true}).pipe(
      tap((session) => this.sessionSubject.next(session))
    );
  }

  logout(): Observable<HubSession> {
    return this.http.post<HubSession>(`${this.apiBase}/logout.php`, {}, {
      headers: this.csrfHeaders(),
      withCredentials: true
    }).pipe(
      tap((session) => this.sessionSubject.next(session))
    );
  }

  setLanguage(language: string): Observable<HubSession> {
    return this.http.post<HubSession>(`${this.apiBase}/language.php`, {language}, {
      headers: this.csrfHeaders(),
      withCredentials: true
    }).pipe(
      tap((session) => this.sessionSubject.next(session))
    );
  }

  listForms(): Observable<FormsResponse> {
    return this.http.get<FormsResponse>(`${this.apiBase}/forms.php`, {withCredentials: true});
  }

  createForm(name: string): Observable<FormsResponse> {
    return this.http.post<FormsResponse>(`${this.apiBase}/forms.php`, {action: 'create', name}, {
      headers: this.csrfHeaders(),
      withCredentials: true
    });
  }

  deleteForm(id: number): Observable<FormsResponse> {
    return this.http.post<FormsResponse>(`${this.apiBase}/forms.php`, {action: 'delete', id}, {
      headers: this.csrfHeaders(),
      withCredentials: true
    });
  }

  loadDonationForm(id: number): Observable<HubDonationFormEditorResponse> {
    return this.http.get<HubDonationFormEditorResponse>(`${this.apiBase}/forms.php`, {
      params: {id},
      withCredentials: true
    });
  }

  saveDonationForm(id: number, payload: Record<string, unknown> | FormData): Observable<HubDonationFormEditorResponse> {
    if (payload instanceof FormData) {
      payload.set('action', 'save');
      payload.set('id', String(id));
      return this.http.post<HubDonationFormEditorResponse>(`${this.apiBase}/forms.php`, payload, {
        headers: this.csrfHeaders(),
        withCredentials: true
      });
    }

    return this.http.post<HubDonationFormEditorResponse>(`${this.apiBase}/forms.php`, {action: 'save', id, ...payload}, {
      headers: this.csrfHeaders(),
      withCredentials: true
    });
  }

  addDonationFormAmount(id: number, forFrequency: 'once' | 'monthly', amount: number): Observable<HubDonationFormEditorResponse> {
    return this.http.post<HubDonationFormEditorResponse>(`${this.apiBase}/forms.php`, {action: 'addAmount', id, forFrequency, amount}, {
      headers: this.csrfHeaders(),
      withCredentials: true
    });
  }

  deleteDonationFormAmount(id: number, amountId: number): Observable<HubDonationFormEditorResponse> {
    return this.http.post<HubDonationFormEditorResponse>(`${this.apiBase}/forms.php`, {action: 'deleteAmount', id, amountId}, {
      headers: this.csrfHeaders(),
      withCredentials: true
    });
  }

  addDonationFormDesignation(id: number, designation: string): Observable<HubDonationFormEditorResponse> {
    return this.http.post<HubDonationFormEditorResponse>(`${this.apiBase}/forms.php`, {action: 'addDesignation', id, designation}, {
      headers: this.csrfHeaders(),
      withCredentials: true
    });
  }

  deleteDonationFormDesignation(id: number, designationId: number): Observable<HubDonationFormEditorResponse> {
    return this.http.post<HubDonationFormEditorResponse>(`${this.apiBase}/forms.php`, {action: 'deleteDesignation', id, designationId}, {
      headers: this.csrfHeaders(),
      withCredentials: true
    });
  }

  listProcesses(): Observable<ProcessesResponse> {
    return this.http.get<ProcessesResponse>(`${this.apiBase}/processes.php`, {withCredentials: true});
  }

  createProcess(name: string): Observable<ProcessesResponse> {
    return this.http.post<ProcessesResponse>(`${this.apiBase}/processes.php`, {action: 'create', name}, {
      headers: this.csrfHeaders(),
      withCredentials: true
    });
  }

  copyProcessToPeer(processDefinitionId: number): Observable<ProcessesResponse> {
    return this.http.post<ProcessesResponse>(`${this.apiBase}/processes.php`, {action: 'copyToPeer', processDefinitionId}, {
      headers: this.csrfHeaders(),
      withCredentials: true
    });
  }

  loadSettings(): Observable<HubSettingsResponse> {
    return this.http.get<HubSettingsResponse>(`${this.apiBase}/settings.php`, {withCredentials: true});
  }

  saveSettings(payload: Record<string, unknown>): Observable<HubSettingsResponse> {
    return this.http.post<HubSettingsResponse>(`${this.apiBase}/settings.php`, {action: 'updateNgo', ...payload}, {
      headers: this.csrfHeaders(),
      withCredentials: true
    });
  }

  uploadLogo(file: File): Observable<HubSettingsResponse> {
    const payload = new FormData();
    payload.set('action', 'uploadLogo');
    payload.set('logoImage', file);
    return this.http.post<HubSettingsResponse>(`${this.apiBase}/settings.php`, payload, {
      headers: this.csrfHeaders(),
      withCredentials: true
    });
  }

  addLanguage(shortCode: string, name: string): Observable<HubSettingsResponse> {
    return this.http.post<HubSettingsResponse>(`${this.apiBase}/settings.php`, {action: 'addLanguage', shortCode, name}, {
      headers: this.csrfHeaders(),
      withCredentials: true
    });
  }

  updateLanguage(shortCode: string, name: string): Observable<HubSettingsResponse> {
    return this.http.post<HubSettingsResponse>(`${this.apiBase}/settings.php`, {action: 'updateLanguage', shortCode, name}, {
      headers: this.csrfHeaders(),
      withCredentials: true
    });
  }

  deleteLanguage(shortCode: string): Observable<HubSettingsResponse> {
    return this.http.post<HubSettingsResponse>(`${this.apiBase}/settings.php`, {action: 'deleteLanguage', shortCode}, {
      headers: this.csrfHeaders(),
      withCredentials: true
    });
  }

  listUsers(): Observable<UsersResponse> {
    return this.http.get<UsersResponse>(`${this.apiBase}/users.php`, {withCredentials: true});
  }

  createUser(email: string, password: string, admin: boolean): Observable<UsersResponse> {
    return this.http.post<UsersResponse>(`${this.apiBase}/users.php`, {action: 'create', email, password, admin}, {
      headers: this.csrfHeaders(),
      withCredentials: true
    });
  }

  setUserRole(id: number, role: 'admin' | 'processAdmin' | 'translator', value: boolean): Observable<UsersResponse> {
    return this.http.post<UsersResponse>(`${this.apiBase}/users.php`, {action: 'setRole', id, role, value}, {
      headers: this.csrfHeaders(),
      withCredentials: true
    });
  }

  setUserPassword(id: number, password: string): Observable<UsersResponse> {
    return this.http.post<UsersResponse>(`${this.apiBase}/users.php`, {action: 'setPassword', id, password}, {
      headers: this.csrfHeaders(),
      withCredentials: true
    });
  }

  deleteUser(id: number): Observable<UsersResponse> {
    return this.http.post<UsersResponse>(`${this.apiBase}/users.php`, {action: 'delete', id}, {
      headers: this.csrfHeaders(),
      withCredentials: true
    });
  }

  loadEbics(): Observable<EbicsResponse> {
    return this.http.get<EbicsResponse>(`${this.apiBase}/ebics.php`, {withCredentials: true});
  }

  saveEbics(ebics: Partial<HubEbicsSettings>): Observable<EbicsResponse> {
    return this.http.post<EbicsResponse>(`${this.apiBase}/ebics.php`, ebics, {
      headers: this.csrfHeaders(),
      withCredentials: true
    });
  }

  listLogs(page = 1, pageSize = 50, type = '', query = ''): Observable<HubLogsResponse> {
    return this.http.get<HubLogsResponse>(`${this.apiBase}/logs.php`, {
      params: {page, pageSize, type, query},
      withCredentials: true
    });
  }

  loadLoginForm(): Observable<HubLoginFormResponse> {
    return this.http.get<HubLoginFormResponse>(`${this.apiBase}/login-form.php`, {withCredentials: true});
  }

  saveLoginForm(translations: Record<string, Record<string, string>>): Observable<HubLoginFormResponse> {
    return this.http.post<HubLoginFormResponse>(`${this.apiBase}/login-form.php`, {translations}, {
      headers: this.csrfHeaders(),
      withCredentials: true
    });
  }

  loadTranslations(scope: 'global' | 'process', processDefinitionId?: number): Observable<HubTranslationsResponse> {
    return this.http.get<HubTranslationsResponse>(`${this.apiBase}/translations.php`, {
      params: processDefinitionId ? {scope, processDefinitionId} : {scope},
      withCredentials: true
    });
  }

  saveTranslation(scope: 'global' | 'process', label: string, format: string, translations: Record<string, string>, processDefinitionId?: number): Observable<HubTranslationsResponse> {
    return this.http.post<HubTranslationsResponse>(`${this.apiBase}/translations.php`, {
      action: 'save',
      scope,
      processDefinitionId,
      label,
      format,
      translations
    }, {
      headers: this.csrfHeaders(),
      withCredentials: true
    });
  }

  createGlobalLabel(label: string): Observable<HubTranslationsResponse> {
    return this.http.post<HubTranslationsResponse>(`${this.apiBase}/translations.php`, {action: 'createGlobal', scope: 'global', label}, {
      headers: this.csrfHeaders(),
      withCredentials: true
    });
  }

  deleteGlobalLabel(label: string): Observable<HubTranslationsResponse> {
    return this.http.post<HubTranslationsResponse>(`${this.apiBase}/translations.php`, {action: 'deleteGlobal', scope: 'global', label}, {
      headers: this.csrfHeaders(),
      withCredentials: true
    });
  }

  translationsExportUrl(scope: 'global' | 'process', processDefinitionId?: number): string {
    const params = new URLSearchParams({action: 'export', scope});
    if (scope === 'process' && processDefinitionId !== undefined) {
      params.set('processDefinitionId', String(processDefinitionId));
    }
    return `${this.apiBase}/translations.php?${params.toString()}`;
  }

  importTranslations(scope: 'global' | 'process', fileContent: string, dryRun: boolean, processDefinitionId?: number): Observable<HubTranslationsImportResponse> {
    return this.http.post<HubTranslationsImportResponse>(`${this.apiBase}/translations.php`, {
      action: 'import',
      scope,
      processDefinitionId,
      fileContent,
      dryRun
    }, {
      headers: this.csrfHeaders(),
      withCredentials: true
    });
  }

  autoTranslate(scope: 'global' | 'process', label: string, language: string, processDefinitionId?: number): Observable<HubTranslationsResponse> {
    return this.http.post<HubTranslationsResponse>(`${this.apiBase}/translations.php`, {
      action: 'autoTranslate',
      scope,
      processDefinitionId,
      label,
      language
    }, {
      headers: this.csrfHeaders(),
      withCredentials: true
    });
  }

  loadProcessEditor(processDefinitionId: number): Observable<HubProcessEditorResponse> {
    return this.http.get<HubProcessEditorResponse>(`${this.apiBase}/process-editor.php`, {
      params: {processDefinitionId},
      withCredentials: true
    });
  }

  saveProcessEditor(processDefinitionId: number, action: string, payload: Record<string, unknown>): Observable<HubProcessEditorResponse> {
    return this.http.post<HubProcessEditorResponse>(`${this.apiBase}/process-editor.php`, {processDefinitionId, action, ...payload}, {
      headers: this.csrfHeaders(),
      withCredentials: true
    });
  }

  translate(key: string): string {
    return this.currentSession?.translations[key] || '';
  }

  private csrfHeaders(): HttpHeaders {
    return new HttpHeaders({[CSRF_HEADER]: this.currentSession?.csrfToken ?? ''});
  }
}
